import { Box } from '@apia/theme';
import React from 'react';
import Config from 'config';
import UploaderContext from './context';
import ApiaUploaderList, {
  IApiaUploaderList,
} from '../../apia/execution/fields/apiaUploader/ApiaUploaderList';
import useContext from '../../../hooks/useContext';
import { isOneClickUploadEnabled } from './UploaderApi';
import { TUploaderFileInfo } from './types';
import { ApiaUtil } from '@apia/components';

const DefaultUploadView = ({
  className,
  ...props
}: {
  className?: string;
} & Partial<IApiaUploaderList> & { noEdit?: boolean }) => {
  const context = useContext(UploaderContext);
  const files = context.shownFiles;
  const [progress, setProgress] = React.useState<IApiaUploaderList['progress']>(
    {},
  );

  const onCheckSignature = React.useCallback<
    NonNullable<
      React.ComponentProps<typeof ApiaUploaderList>['onCheckSignature']
    >
  >(
    (file) => {
      context.api.checkSignature(file);
    },
    [context.api],
  );

  const onDownloadFile = React.useCallback<
    React.ComponentProps<typeof ApiaUploaderList>['onDownloadFile']
  >((file) => void context.api.downloadDocument(file.docId), [context.api]);

  const onDrop = React.useCallback<
    React.ComponentProps<typeof ApiaUploaderList>['onDrop']
  >(
    async (file, newFile) => {
      if ((file.name || file.docName) === newFile.name) {
        await context.api.version(
          file,
          (newProgress) => {
            setProgress((current) => ({
              ...current,
              [file.docId]: newProgress,
            }));
          },
          {
            newFiles: [newFile],
            langId: context.langId,
            translatingFile: context.translatingFile,
          },
        );
      } else {
        ApiaUtil.instance.notifications.notify({
          message: Config.LBL_DROP_FILE_NAMES_NOT_MATCH,
          type: 'warning',
        });
      }
    },
    [context.api, context.langId, context.translatingFile],
  );

  const onEditFile = React.useCallback<
    NonNullable<React.ComponentProps<typeof ApiaUploaderList>['onEditFile']>
  >((file) => void context.api.editDocument(file.docId), [context.api]);

  const onLockFile = React.useCallback<
    NonNullable<React.ComponentProps<typeof ApiaUploaderList>['onLockFile']>
  >(
    (file) => {
      void context.api.lockDocument(file.docId);
    },
    [context.api],
  );

  const onRemoveFile = React.useCallback<
    NonNullable<React.ComponentProps<typeof ApiaUploaderList>['onRemoveFile']>
  >(
    (file) => {
      void context.api.deleteDocument(file.docId, context.langId);
    },
    [context.api, context.langId],
  );

  const onSignFile = React.useCallback<
    NonNullable<React.ComponentProps<typeof ApiaUploaderList>['onSignFile']>
  >(
    (file) => {
      void context.api.markFileToSign(file.docId, context.langId);
    },
    [context.api, context.langId],
  );

  const onVersion = React.useCallback<
    NonNullable<React.ComponentProps<typeof ApiaUploaderList>['onVersion']>
  >(
    (file) => {
      void context.api.version(
        file,
        (newProgress) => {
          setProgress((current) => ({
            ...current,
            [file.docId]: newProgress,
          }));
        },
        { langId: context.langId, translatingFile: context.translatingFile },
      );
    },
    [context.api, context.langId, context.translatingFile],
  );

  const isReadonly = context.isReadonly || props.isReadonly;
  const isDisabled = context.disabled || props.isDisabled;

  const onTranslate = React.useMemo(
    () =>
      context.api?.allowTranslations && context.langId === undefined
        ? (file: TUploaderFileInfo) => context.api?.openTranslationsModal(file)
        : undefined,
    [context.api, context.langId],
  );

  return files.length > 0 ? (
    <Box
      {...window.getVariant('layout.execution.components.uploader.defaultView')}
      className={className}
    >
      <ApiaUploaderList
        forGrid={false}
        onDownloadFile={onDownloadFile}
        onDrop={onDrop}
        onCheckSignature={onCheckSignature}
        onEditFile={onEditFile}
        onLockFile={onLockFile}
        onRemoveFile={onRemoveFile}
        onSignFile={onSignFile}
        onTranslateFile={onTranslate}
        onVersion={onVersion}
        uploadedList={files}
        {...props}
        noErase={
          props.noErase ||
          (window.isEntityViewMode && context.type === 'E') ||
          isReadonly ||
          isDisabled
        }
        noLock={
          props.noLock ||
          (window.isEntityViewMode && context.type === 'E') ||
          isReadonly ||
          isDisabled
        }
        noModify={
          (window.isEntityViewMode && context.type === 'E') ||
          isReadonly ||
          isDisabled ||
          props.noModify ||
          props.noEdit
        }
        noSign={
          props.noSign ||
          (window.isEntityViewMode && context.type === 'E') ||
          isReadonly ||
          isDisabled
        }
        noUpload={
          isOneClickUploadEnabled(
            context.api.configurationObject.oneClickUpload,
          ) ||
          isReadonly ||
          isDisabled ||
          props.noModify
        }
        noVersion={isReadonly || isDisabled || props.noModify}
        progress={progress}
      />
    </Box>
  ) : null;
};

export default React.memo(DefaultUploadView);
